import React, { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { ActionButton, Buttons, CancelButton, Checkbox, Drawer, Form, FormMessage, Icon } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useAuth, useDirtyCheck, useForm } from '~/hooks';
import { colors } from '~/styles';
import { arrayMove } from '~/utils';

const UL = styled.ul`
  margin-top: -0.375rem;
`;

const Row = styled.li`
  position: relative;
  display: flex;
  align-items: center;
  padding: 0.375rem 0;

  ${({ $dragging }) =>
    $dragging &&
    css`
      > * {
        transition: 150ms all ease-in-out;
        opacity: 0.5;
      }
    `}

  ${({ $over, $after }) =>
    $over &&
    css`
      &::before {
        content: ' ';
        position: absolute;
        width: 100%;
        height: 0.0625rem;
        left: 0;
        border-top: 0.125rem solid ${colors.primary};

        ${
          $after
            ? css`
                bottom: -0.0625rem;
              `
            : css`
                top: -0.0625rem;
              `
        }
      }
    `}
`;

const Handle = styled.div`
  cursor: move;
  font-size: 1rem;
  color: ${colors.grey40};
  transition: 150ms all ease-in-out;
  padding: 0 0.5rem 0 0.25rem;
  display: flex;
  align-items: center;

  &:hover {
    color: ${colors.primary};
  }
`;

export default function WidgetSettings({ onClose, onSaved }) {
  const api = useApi();
  const auth = useAuth();
  const { workspace } = useWorkspace();

  const widgets = useMemo(
    () =>
      [
        { key: 'services_revenue', name: 'Services Revenue', hidden: !auth.executiveDashboard.servicesRevenue },
        {
          key: 'services_gross_profit',
          name: 'Services Gross Profit',
          hidden: !auth.executiveDashboard.servicesGrossProfit,
        },
        {
          key: 'services_gross_margin',
          name: 'Services Gross Margin',
          hidden: !auth.executiveDashboard.servicesGrossMargin,
        },
        {
          key: 'billable_utilization',
          name: 'Billable Utilization',
          hidden: !auth.executiveDashboard.billableUtilization,
        },
        {
          key: 'top_clients_by_services_revenue',
          name: 'Top Clients by Services Revenue',
          hidden: !auth.executiveDashboard.topClientsByServicesRevenue,
        },
        {
          key: 'top_clients_by_billable_hours',
          name: 'Top Clients by Billable Hours',
          hidden: !auth.executiveDashboard.topClientsByBillableHours,
        },
        {
          key: 'services_effective_bill_rate',
          name: 'Services Effective Bill Rate',
          hidden: !auth.executiveDashboard.servicesEffectiveBillRate,
        },
        {
          key: 'services_realization_rate',
          name: 'Services Realization Rate',
          hidden: !auth.executiveDashboard.servicesRealizationRate,
        },
        {
          key: 'client_billable_hours',
          name: 'Client Billable Hours',
          hidden: !auth.executiveDashboard.clientBillableHours,
        },
        {
          key: 'client_non_billable_hours',
          name: 'Client Non-Billable Hours',
          hidden: !auth.executiveDashboard.clientNonBillableHours,
        },
        {
          key: 'most_healthy_projects',
          name: 'Most Healthy Projects',
          hidden: !auth.executiveDashboard.mostHealthyProjects,
        },
        {
          key: 'least_healthy_projects',
          name: 'Least Healthy Projects',
          hidden: !auth.executiveDashboard.leastHealthyProjects,
        },
        {
          key: 'pipeline_by_expected_close_date',
          name: 'Pipeline By Expected Close Date',
          hidden: !auth.executiveDashboard.pipelineByExpectedCloseDate,
        },
      ].filter(({ hidden }) => !hidden),
    [auth],
  );

  const [list, setList] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const dirtyCheck = useDirtyCheck(() => isDirty);
  const [{ status, message, isSubmitting, saved }, form] = useForm();

  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).executiveDashboard().widgets().get();

      setList([
        ...data
          .map((row) => {
            const widget = widgets.find(({ key }) => key === row.key);
            return widget ? { key: widget.key, name: widget.name, visible: row.visible } : null;
          })
          .filter(Boolean),
        ...widgets
          .filter((widget) => !data.some((row) => row.key === widget.key))
          .map((widget) => ({ key: widget.key, name: widget.name, visible: false })),
      ]);
    })();
  }, [api, workspace.id, widgets]);

  const [drag, setDrag] = useState({ index: null, over: null });

  const handleDragStart = (index) => {
    setDrag({ index, over: null });
  };

  const handleDragEnd = () => {
    setDrag({ index: null, over: null });
  };

  const handleDragOver = (index) => {
    if (!drag || drag.over === index) {
      return;
    }
    setDrag({ ...drag, over: index });
    return false;
  };

  const handleDrop = (index) => {
    // On downward drags the indicator renders below the hovered row ($after), so land after it.
    setList(arrayMove(list, drag.index, drag.index < index ? index + 1 : index));
    setIsDirty(true);
  };

  const handleSubmit = async () => {
    try {
      form.submit();
      await api.www
        .workspaces(workspace.id)
        .executiveDashboard()
        .widgets()
        .save(list.map(({ key, visible }) => ({ key, visible })));
      await onSaved();
      form.done();
    } catch (error) {
      form.error({ message: error.message });
    }
  };

  return (
    <Drawer
      isOpen
      title="Personalize Dashboard"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        return (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {status && <FormMessage.Error>{message}</FormMessage.Error>}

            <Form.Section title="Dashboard Widgets">
              <UL>
                {list?.map((widget, index) => (
                  <Row
                    key={widget.key}
                    draggable={index === drag?.index}
                    $dragging={index === drag?.index}
                    $over={index === drag?.over && index !== drag?.index}
                    $after={drag?.index !== null && drag?.index < index}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => {
                      e.preventDefault();
                      handleDragOver(index);
                    }}
                    onDrop={() => handleDrop(index)}>
                    <Handle onMouseDown={() => handleDragStart(index)} onMouseUp={handleDragEnd}>
                      <Icon icon="grip-vertical" />
                    </Handle>

                    <Checkbox
                      label={widget.name}
                      checked={widget.visible}
                      onChange={({ target: { checked } }) => {
                        setList((prev) => prev.map((item, i) => (i === index ? { ...item, visible: checked } : item)));
                        setIsDirty(true);
                      }}
                    />
                  </Row>
                ))}
              </UL>
            </Form.Section>

            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                <ActionButton isLoading={isSubmitting} ok={saved} type="submit" onClick={() => handleSubmit()}>
                  Save &amp; Close
                </ActionButton>
              </Buttons>
            </Drawer.Actions>
          </div>
        );
      }}
    </Drawer>
  );
}
